/**
 * Datetime utilities for timezone-aware date/time operations
 * Used by the calendar system to ensure consistent date key generation
 */
/**
 * Convert a UTC ISO string to a local Date object in the specified timezone
 * @param isoUtc - UTC ISO string (e.g., "2024-01-15T22:00:00.000Z")
 * @param tz - IANA timezone (e.g., "America/Los_Angeles")
 * @returns Local Date object
 */
export function toLocal(isoUtc: string, tz: string): Date {
  const utcDate = new Date(isoUtc)
  // Use Intl.DateTimeFormat to get the local time in the specified timezone
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
  const parts = formatter.formatToParts(utcDate)
  const dateParts: Record<string, string> = {}
  for (const part of parts) {
    if (part.type !== 'literal') {
      dateParts[part.type] = part.value
    }
  }
  // Create a local Date object (in browser's timezone)
  // This is used for display purposes
  return new Date(
    parseInt(dateParts.year),
    parseInt(dateParts.month) - 1,
    parseInt(dateParts.day),
    parseInt(dateParts.hour),
    parseInt(dateParts.minute),
    parseInt(dateParts.second),
  )
}
/**
 * Convert a local date/time to UTC ISO string
 * @param localISO - Local ISO string (e.g., "2024-01-15T14:00:00")
 * @param tz - IANA timezone (e.g., "America/Los_Angeles")
 * @returns UTC ISO string
 */
export function toUtcISOString(localISO: string, tz: string): string {
  // Parse the local date string
  const localDate = new Date(localISO)
  // Get the timezone offset in minutes
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
  // Format the date in the target timezone
  const parts = formatter.formatToParts(localDate)
  const dateParts: Record<string, string> = {}
  for (const part of parts) {
    if (part.type !== 'literal') {
      dateParts[part.type] = part.value
    }
  }
  // Create a UTC date from the local parts
  const utcDate = new Date(
    Date.UTC(
      parseInt(dateParts.year),
      parseInt(dateParts.month) - 1,
      parseInt(dateParts.day),
      parseInt(dateParts.hour),
      parseInt(dateParts.minute),
      parseInt(dateParts.second),
    ),
  )
  // Calculate the offset between the local time and UTC
  const localTime = localDate.getTime()
  const utcTime = utcDate.getTime()
  const offset = localTime - utcTime
  // Apply the offset to get the correct UTC time
  const correctedUtcDate = new Date(localDate.getTime() - offset)
  return correctedUtcDate.toISOString()
}
/**
 * Convert a UTC ISO string to a local date key in YYYY-MM-DD format
 * FIXED: Now uses direct extraction to ensure correct date keys across all timezones
 * @param isoUtc - UTC ISO string (e.g., "2024-01-15T22:00:00.000Z")
 * @param tz - IANA timezone (e.g., "America/Los_Angeles")
 * @returns Date key in YYYY-MM-DD format (e.g., "2024-01-15")
 */
export function ymdLocal(isoUtc: string, tz: string): string {
  const date = new Date(isoUtc)
  // Use Intl.DateTimeFormat to get the date parts in the target timezone
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
  // Extract the parts directly without creating a Date object
  const parts = formatter.formatToParts(date)
  const year = parts.find((p) => p.type === 'year')!.value
  const month = parts.find((p) => p.type === 'month')!.value
  const day = parts.find((p) => p.type === 'day')!.value
  return `${year}-${month}-${day}`
}
/**
 * Validate that a date range is valid (start before end)
 * @param startAt - Start date ISO string
 * @param endAt - End date ISO string
 * @throws Error if range is invalid
 */
export function validateRange(startAt: string, endAt: string): void {
  const start = new Date(startAt)
  const end = new Date(endAt)
  if (isNaN(start.getTime())) {
    throw new Error(`Invalid start date: ${startAt}`)
  }
  if (isNaN(end.getTime())) {
    throw new Error(`Invalid end date: ${endAt}`)
  }
  if (start >= end) {
    throw new Error(
      `Invalid date range: start (${startAt}) must be before end (${endAt})`,
    )
  }
}
